import React, { createContext, useContext, useState, useEffect } from 'react';
import AuthService from '../services/AuthService';

interface User {
  _id: string;
  username: string;
  email: string;
  profilePicture?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  setUser: (user: User | null) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const localUser = AuthService.getUser();
        if (localUser) {
          // Verify token with backend
          const response = await AuthService.verifyToken();
          if (response.success) {
            setUser(response.data.user);
            AuthService.saveUser(response.data.user);
          } else {
            setUser(null);
            AuthService.removeUser();
          }
        } else {
          // Try to verify with backend (cookie might exist)
          const response = await AuthService.verifyToken();
          if (response.success) {
            setUser(response.data.user);
            AuthService.saveUser(response.data.user);
          }
        }
      } catch (error) {
        console.error('Auth init error:', error);
        setUser(null);
        AuthService.removeUser();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const logout = async () => {
    await AuthService.logout();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, setUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
